import Phaser from 'phaser';
import { PALETTE } from '../config';
import { AudioEngine } from '../game/AudioEngine';
import { SaveManager } from '../game/SaveManager';
import { Ads } from '../game/ads';
import { STARTER_ROSTER } from '../game/data/athletes';
import { getCosmetic, variantKey, variantUrl } from '../game/data/cosmetics';

// Bump when any public (non-hashed) sprite/background/ui asset is re-exported, to bust
// browser caches on stable filenames. v2: re-cut penguin/octopus backgrounds.
// v3: re-cut lemur (green shorts were keyed out with the mint backdrop).
// v4: re-cut lemur again — killed the faint contact-shadow/haze that floated the sprite.
export const ASSET_V = '4';

/**
 * Preload: shows a progress bar and generates shared procedural textures (particles).
 * The MVP ships zero external art — Higgsfield sprites/backgrounds drop in here later
 * via this.load.atlas / this.load.image with no scene-flow changes.
 */
export class Preload extends Phaser.Scene {
  constructor() {
    super('Preload');
  }

  preload(): void {
    this.drawProgressBar();
    // Cache-bust public art whose filenames are stable (they live in public/, so they are
    // NOT content-hashed by Vite). Bump ASSET_V whenever a sprite/background is re-exported,
    // otherwise browsers that cached the old file keep serving it. See nginx no-cache too.
    const v = (p: string): string => `${p}?v=${ASSET_V}`;
    // Higgsfield art (cartoon-3D). Sprites are pre-cut (transparent) via scripts/cutout.mjs.
    this.load.image('cheetah_sprite', v('assets/sprites/cheetah.png'));
    this.load.image('sloth_sprite', v('assets/sprites/sloth.png'));
    this.load.image('gorilla_sprite', v('assets/sprites/gorilla.png'));
    // NOTE: the stadium race backdrop + all rig PARTS are NOT loaded here — they're only needed
    // in-race, so BackgroundLoad fetches them after the menu shows. This keeps the INITIAL download
    // (what CrazyGames measures up to loadingStop) under their mobile-homepage 20MB budget.
    // UI art (Higgsfield).
    if (!this.textures.exists('logo')) this.load.image('logo', v('assets/ui/logo.png'));
    this.load.image('menu_hero', v('assets/backgrounds/menu_hero.png'));
    this.load.image('card_sprint_100m', v('assets/ui/card_sprint.png'));
    this.load.image('card_long_jump', v('assets/ui/card_longjump.png'));
    this.load.image('card_hurdles', v('assets/ui/card_hurdles.png'));
    this.load.image('card_javelin', v('assets/ui/card_javelin.png'));
    this.load.image('card_high_jump', v('assets/ui/card_high_jump.png'));
    this.load.image('card_triple_jump', v('assets/ui/card_triple_jump.png'));
    this.load.image('card_shot_put', v('assets/ui/card_shot_put.png'));
    this.load.image('card_hammer_throw', v('assets/ui/card_hammer_throw.png'));
    this.load.image('card_pole_vault', v('assets/ui/card_pole_vault.png'));
    this.load.image('card_discus', v('assets/ui/card_discus.png'));
    this.load.image('card_weightlifting', v('assets/ui/card_weightlifting.png'));
    this.load.image('card_swim_50m', v('assets/ui/card_swim_50m.png'));
    this.load.image('card_diving', v('assets/ui/card_diving.png'));
    this.load.image('card_archery', v('assets/ui/card_archery.png'));
    // Head-cosmetic baked variants: only precache the ones the player has EQUIPPED (a handful),
    // not all 45 — they're full-size sprites. Newly-equipped ones load on demand in the shop.
    this.preloadEquippedVariants(v);
    // Whole-body hero sprites (perfectly composed) for static portraits in menus/select.
    for (const a of ['cheetah', 'sloth', 'gorilla', 'capybara', 'meerkat', 'ostrich', 'kangaroo', 'hare', 'elephant', 'frog', 'penguin', 'bear', 'lemur', 'octopus', 'wolf']) {
      this.load.image(`${a}_full`, v(`assets/sprites/full/${a}.png`));
    }
    // (Articulated rig parts moved to BackgroundLoad — see note above.)
    // Head crops for sprite-only species (no rig): gives them a proper leaderboard head
    // icon via the same `_part_head` path the rigged species use (a runtime circle mask
    // is unreliable, so we pre-crop the head offline — see scripts/crophead.mjs).
    for (const a of ['frog', 'penguin', 'bear', 'lemur', 'octopus', 'wolf']) {
      this.load.image(`${a}_part_head`, v(`assets/sprites/heads/${a}.png`));
    }
  }

  /** Queue the baked-variant sprite for each athlete that has a head cosmetic equipped. */
  private preloadEquippedVariants(v: (p: string) => string): void {
    let save: SaveManager | undefined;
    try { save = SaveManager.instance; } catch { return; }
    if (!save?.hasProfile()) return;
    for (const athlete of STARTER_ROSTER) {
      const equipped = save.equippedFor(athlete.id);
      const cos = equipped.head ? getCosmetic(equipped.head) : undefined;
      if (!cos?.accessory) continue;
      const key = variantKey(athlete.skin, cos.accessory);
      if (!this.textures.exists(key)) this.load.image(key, v(variantUrl(athlete.skin, cos.accessory)));
    }
  }

  create(): void {
    this.makeParticleTextures();
    this.loadSfx();
    // Wait for the flag font (so the first flag-bearing scene renders flags, not letters), but
    // never block the boot for long if it's slow/unavailable.
    const go = (): void => {
      Ads.loadingStop();
      this.scene.launch('BackgroundLoad'); // heavy in-race assets load in parallel, after the menu
      this.scene.start('MainMenu');
    };
    Promise.race([
      document.fonts.load("16px 'Twemoji Country Flags'", '🇮🇹').catch(() => undefined),
      new Promise((r) => setTimeout(r, 1200)),
    ]).finally(go);
  }

  /**
   * Best-effort load of recorded SFX (e.g. ElevenLabs mp3s) from assets/audio/sfx/.
   * Each call fails silently if the file is absent, so the procedural AudioEngine keeps
   * working until the assets are added — then they activate automatically.
   */
  private loadSfx(): void {
    for (const name of ['whistle', 'crowd', 'fanfare', 'thud', 'chime', 'tap', 'whoosh', 'beat', 'countdown']) {
      void AudioEngine.loadSample(name, `assets/audio/sfx/${name}.mp3`);
    }
  }

  private drawProgressBar(): void {
    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;
    const w = Math.min(420, this.scale.width * 0.7);

    // Brand logo (loaded in Boot) instead of plain text; gentle floating bob.
    if (this.textures.exists('logo')) {
      const logo = this.add.image(cx, cy - 70, 'logo').setOrigin(0.5);
      logo.setScale(Math.min((this.scale.width * 0.62) / logo.width, (this.scale.height * 0.4) / logo.height));
      this.tweens.add({ targets: logo, y: cy - 78, duration: 1300, yoyo: true, repeat: -1, ease: 'Sine.inOut' });
    } else {
      this.add.text(cx, cy - 60, 'ZOOLYMPICS', { fontFamily: '"Twemoji Country Flags", system-ui, sans-serif', fontSize: '48px', color: '#f4f7ff', fontStyle: 'bold' }).setOrigin(0.5);
    }

    const caption = this.add.text(cx, cy + 34, 'Caricamento…', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: '15px', color: '#9fb0e0' }).setOrigin(0.5);
    const bar = this.add.graphics();
    this.load.on('progress', (p: number) => {
      bar.clear();
      bar.fillStyle(PALETTE.ink, 0.5).fillRoundedRect(cx - w / 2 - 3, cy - 3, w + 6, 24, 12);
      bar.fillStyle(PALETTE.trackLine, 1).fillRoundedRect(cx - w / 2, cy, w, 18, 9);
      bar.fillStyle(PALETTE.laneAccent, 1).fillRoundedRect(cx - w / 2, cy, w * p, 18, 9);
      caption.setText(`Caricamento… ${Math.round(p * 100)}%`);
    });
  }

  /** A soft circle for dust/confetti particle systems. */
  private makeParticleTextures(): void {
    if (!this.textures.exists('spark')) {
      const g = this.make.graphics({ x: 0, y: 0 });
      g.fillStyle(0xffffff, 1);
      g.fillCircle(16, 16, 16);
      g.generateTexture('spark', 32, 32);
      g.destroy();
    }
    if (!this.textures.exists('confetti')) {
      const g = this.make.graphics({ x: 0, y: 0 });
      g.fillStyle(0xffffff, 1);
      g.fillRect(0, 0, 14, 9);
      g.generateTexture('confetti', 14, 9);
      g.destroy();
    }
  }
}
